import { useState, useEffect, useCallback } from "react";
import { X, History, PlusCircle, Pencil, Trash2, Loader2, User, Clock } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const actionConfig = {
  create: {
    icon: PlusCircle,
    label: "Created",
    color: "bg-emerald-50 text-emerald-700 border-emerald-200",
    iconColor: "text-emerald-600",
  },
  update: {
    icon: Pencil,
    label: "Updated",
    color: "bg-blue-50 text-blue-700 border-blue-200",
    iconColor: "text-blue-600",
  },
  delete: {
    icon: Trash2,
    label: "Deleted",
    color: "bg-red-50 text-red-700 border-red-200",
    iconColor: "text-red-600",
  },
};

const formatDate = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AuditLogEntry = ({ log }) => {
  const [expanded, setExpanded] = useState(false);
  const config = actionConfig[log.action] || actionConfig.update;
  const Icon = config.icon;
  const hasChanges = log.changes && Object.keys(log.changes).length > 0;

  return (
    <div className="border border-slate-100 rounded-xl p-4 hover:border-slate-200 transition-colors" data-testid={`audit-log-entry-${log.id}`}>
      <div className="flex items-start gap-3">
        <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0", config.color.split(" ").slice(0, 1).join(" "))}>
          <Icon className={cn("w-4 h-4", config.iconColor)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={cn("inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full border", config.color)}>
                  {config.label}
                </span>
                <span className="text-sm font-semibold text-slate-900 truncate">
                  {log.usecase_title}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {log.user}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDate(log.timestamp)}
                </span>
              </div>
            </div>
          </div>

          {hasChanges && (
            <div className="mt-3">
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs text-[#FF6F40] hover:underline font-medium"
              >
                {expanded ? "Hide" : "Show"} {Object.keys(log.changes).length} field change{Object.keys(log.changes).length !== 1 ? "s" : ""}
              </button>
              {expanded && (
                <div className="mt-2 space-y-2 bg-slate-50 rounded-lg p-3 border border-slate-100">
                  {Object.entries(log.changes).map(([field, diff]) => (
                    <div key={field} className="text-xs">
                      <div className="font-semibold text-slate-700 mb-1">{field}</div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-red-50 border border-red-100 rounded p-2 text-red-700 break-words">
                          <div className="text-[10px] uppercase tracking-wide text-red-500 font-semibold mb-1">Before</div>
                          {Array.isArray(diff.before)
                            ? diff.before.join(", ") || "—"
                            : String(diff.before || "—").substring(0, 200)}
                        </div>
                        <div className="bg-emerald-50 border border-emerald-100 rounded p-2 text-emerald-700 break-words">
                          <div className="text-[10px] uppercase tracking-wide text-emerald-600 font-semibold mb-1">After</div>
                          {Array.isArray(diff.after)
                            ? diff.after.join(", ") || "—"
                            : String(diff.after || "—").substring(0, 200)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const AuditLogModal = ({ onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/audit-logs`);
      setLogs(response.data);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const filteredLogs = filter === "all" ? logs : logs.filter((l) => l.action === filter);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 modal-backdrop"
      onClick={handleBackdropClick}
      data-testid="audit-log-modal"
    >
      <div className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#FFF1EC] rounded-xl flex items-center justify-center">
                <History className="w-5 h-5 text-[#FF6F40]" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-900 font-['Manrope']">
                  Audit Log
                </h2>
                <p className="text-sm text-slate-500">
                  {logs.length} {logs.length === 1 ? "entry" : "entries"} recorded
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
              data-testid="audit-log-close-btn"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-2 mt-4">
            {[
              { key: "all", label: "All" },
              { key: "create", label: "Created" },
              { key: "update", label: "Updated" },
              { key: "delete", label: "Deleted" },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={cn(
                  "px-3 py-1.5 text-xs font-semibold rounded-full transition-colors",
                  filter === tab.key
                    ? "bg-[#FF6F40] text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                )}
                data-testid={`audit-filter-${tab.key}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <ScrollArea className="h-[calc(90vh-160px)]">
          <div className="p-6">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 text-[#FF6F40] animate-spin" />
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                  <History className="w-8 h-8 text-slate-400" />
                </div>
                <p className="text-slate-500 text-sm">
                  {filter === "all" ? "No activity recorded yet." : `No ${filter} events found.`}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredLogs.map((log) => (
                  <AuditLogEntry key={log.id} log={log} />
                ))}
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default AuditLogModal;
